/**
 * VisualLifecycleMonitor — timeline view of combat / VFX events,
 * with duplicate detection to debug double-fire visual bugs.
 *
 * Primary use case: "Why does my projectile impact VFX play twice?"
 * Typical answer surfaced here: optimistic hit_vfx at T+0ms and
 * confirmed impact_feedback at T+80ms for the same target.
 *
 * Phase 3: folded into the unified window engine — exported as a CONTENT
 * component (body only). WindowHost owns the ToolWindow chrome + open/close
 * (frozen geometry key `toolwindow_visual-lifecycle-monitor`).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  VisualLifecycleStore,
  type VisualEvent,
  type VisualEventType,
} from '../../diagnostics/VisualLifecycleStore';
import { gameLogger } from '../../../utils/GameLogger';

const ALL_TYPES: VisualEventType[] = [
  'projectile_spawn',
  'projectile_impact',
  'ground_impact_vfx',
  'hit_vfx',
  'impact_feedback',
  'fct_damage',
  'fct_heal',
  'screen_shake',
  'hitstop',
  'sprite_flash',
  'hit_feel',
  'chain_vfx',
  'aoe_vfx',
  'ability_cast_vfx',
  'status_vfx',
  'reaction_vfx',
];

function typeColor(type: VisualEventType): string {
  switch (type) {
    case 'projectile_spawn': return '#74c0fc';
    case 'projectile_impact': return '#cc88ff';
    case 'ground_impact_vfx': return '#a9a9a9';
    case 'hit_vfx': return '#ff8855';
    case 'impact_feedback': return '#ff6b6b';
    case 'fct_damage': return '#ffd23f';
    case 'fct_heal': return '#51cf66';
    case 'screen_shake': return '#e1b400';
    case 'hitstop': return '#ff9f43';
    case 'sprite_flash': return '#feca57';
    case 'hit_feel': return '#feca57';
    case 'chain_vfx': return '#54a0ff';
    case 'aoe_vfx': return '#5f27cd';
    case 'ability_cast_vfx': return '#00d2d3';
    case 'status_vfx': return '#ff9ff3';
    case 'target_status_pips': return '#a3e635';
    case 'reaction_vfx': return '#7bed9f';
    case 'death_vfx': return '#b39ddb';
    case 'ground_mark': return '#8d6e63';
    case 'atmos_pulse': return '#4dd0e1';
    case 'trail_vfx': return '#f48fb1';
    case 'zone_expired': return '#26a69a';
  }
}

function typeLabel(type: VisualEventType): string {
  switch (type) {
    case 'projectile_spawn': return 'Proj Spawn';
    case 'projectile_impact': return 'Proj Impact';
    case 'ground_impact_vfx': return 'Ground Impact';
    case 'hit_vfx': return 'Hit VFX';
    case 'impact_feedback': return 'Impact FB';
    case 'fct_damage': return 'FCT Dmg';
    case 'fct_heal': return 'FCT Heal';
    case 'screen_shake': return 'Shake';
    case 'hitstop': return 'Hitstop';
    case 'sprite_flash': return 'Flash';
    case 'hit_feel': return 'Hit Feel';
    case 'chain_vfx': return 'Chain';
    case 'aoe_vfx': return 'AoE';
    case 'ability_cast_vfx': return 'Cast VFX';
    case 'status_vfx': return 'Status';
    case 'target_status_pips': return 'Tgt Pips';
    case 'reaction_vfx': return 'Reaction';
    case 'death_vfx': return 'Death';
    case 'ground_mark': return 'Grd Mark';
    case 'atmos_pulse': return 'Atmos';
    case 'trail_vfx': return 'Trail';
    case 'zone_expired': return 'Zone End';
  }
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}.${Math.floor(d.getMilliseconds() / 10).toString().padStart(2, '0')}`;
}

/**
 * Serialize the visible visual events to a clipboard-friendly digest.
 * Header line with totals, then one line per event — duplicate markers
 * inline so a "why does this fire twice?" trace pastes straight into a
 * bug report or to an agent. Mirrors the AbilityLifecycleMonitor copy.
 *
 *   Visual Lifecycle — showing 12 of 42 events, 3 duplicates
 *   30:45.12  Proj Spawn     fireball → pig_3
 *   30:45.20  Hit VFX        fireball impact   [DUP of evt_117]
 */
export function serializeVisualEventsForCopy(
  events: VisualEvent[],
  meta: { totalEvents: number; dupCount: number },
): string {
  const head = `Visual Lifecycle — showing ${events.length} of ${meta.totalEvents} events, ` +
    `${meta.dupCount} duplicate${meta.dupCount === 1 ? '' : 's'}`;
  if (events.length === 0) return `${head}\n(no events)`;
  // `+Δms` is relative to the first shown event so timing desyncs (e.g.
  // feedback firing before the projectile lands) are obvious; `[source]`
  // tags the pipeline path (optimistic | confirmed | projectile) so
  // double-fires read at a glance.
  const t0 = events[0].timestamp;
  const lines = events.map((e) => {
    const dt = `+${e.timestamp - t0}ms`.padStart(7);
    const src = (e.source ? `[${e.source}]` : '').padEnd(13);
    const dup = e.isDuplicate ? `   [DUP of ${e.duplicateOf ?? 'unknown'}]` : '';
    return `${formatTime(e.timestamp)} ${dt}  ${typeLabel(e.type).padEnd(13)} ${src} ${e.label}${dup}`;
  });
  return [head, ...lines].join('\n');
}

function FilterToggle({
  type,
  active,
  count,
  onToggle,
}: {
  type: VisualEventType;
  active: boolean;
  count: number;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        background: active ? `${typeColor(type)}22` : 'rgba(255,255,255,0.04)',
        border: `1px solid ${active ? typeColor(type) : 'rgba(255,255,255,0.08)'}`,
        color: active ? typeColor(type) : '#888',
        borderRadius: 3,
        padding: '2px 6px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: typeColor(type),
        }}
      />
      {typeLabel(type)}
      <span style={{ opacity: 0.6 }}>{count}</span>
    </button>
  );
}

function EventRow({ event, isLatest }: { event: VisualEvent; isLatest: boolean }): React.ReactElement {
  const color = typeColor(event.type);
  const borderLeft = event.isDuplicate ? '3px solid #ff6b6b' : `3px solid ${color}`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '4px 8px',
        borderLeft,
        background: isLatest ? 'rgba(255,255,255,0.03)' : 'transparent',
        fontSize: 11,
        fontFamily: 'monospace',
      }}
    >
      <span style={{ color: '#666', minWidth: 52 }}>{formatTime(event.timestamp)}</span>
      <span
        style={{
          color,
          minWidth: 80,
          fontWeight: 600,
        }}
      >
        {typeLabel(event.type)}
      </span>
      <span style={{ color: '#ccc', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {event.label}
      </span>
      {event.isDuplicate && (
        <span
          style={{
            background: '#ff6b6b22',
            color: '#ff6b6b',
            padding: '1px 4px',
            borderRadius: 3,
            fontSize: 9,
            whiteSpace: 'nowrap',
          }}
          title={`Duplicate of ${event.duplicateOf || 'unknown'}`}
        >
          DUP
        </span>
      )}
    </div>
  );
}

export function VisualLifecycleMonitorContent(): React.ReactElement {
  const [activeTypes, setActiveTypes] = useState<Set<VisualEventType>>(
    () => new Set(ALL_TYPES),
  );
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [tick, setTick] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const unsub = VisualLifecycleStore.subscribe(() => setTick((t) => t + 1));
    return unsub;
  }, []);

  const events = VisualLifecycleStore.all();
  const filtered = useMemo(() => {
    let list = events.filter((e) => activeTypes.has(e.type));
    if (showDuplicatesOnly) {
      list = list.filter((e) => e.isDuplicate);
    }
    return list.slice(-80);
  }, [events, activeTypes, showDuplicatesOnly, tick]);

  const counts = useMemo(() => VisualLifecycleStore.countByType(), [tick]);
  const dupCount = useMemo(() => VisualLifecycleStore.duplicateCount(), [tick]);

  const toggleType = useCallback((type: VisualEventType) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setActiveTypes((prev) =>
      prev.size === ALL_TYPES.length ? new Set() : new Set(ALL_TYPES),
    );
  }, []);

  const clear = useCallback(() => {
    VisualLifecycleStore.clear();
  }, []);

  // Copy the visible (post-filter) event list. Scoped to `filtered` so DJ
  // can narrow to one type (or "Dups only") then copy just that subset —
  // same affordance as AbilityLifecycleMonitor's copy.
  const copyAll = useCallback(() => {
    const text = serializeVisualEventsForCopy(filtered, {
      totalEvents: events.length,
      dupCount,
    });
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      (err) => gameLogger.warn('ui', `[VisualLifecycleMonitor] clipboard write failed: ${String(err)}`),
    );
  }, [filtered, events.length, dupCount]);

  return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div
          style={{
            padding: '8px 10px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#ddd' }}>
              Events: {events.length}
            </span>
            {dupCount > 0 && (
              <span
                style={{
                  background: '#ff6b6b22',
                  color: '#ff6b6b',
                  fontSize: 10,
                  padding: '2px 5px',
                  borderRadius: 3,
                }}
              >
                {dupCount} duplicates
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => setShowDuplicatesOnly((v) => !v)}
              style={{
                background: showDuplicatesOnly ? '#ff6b6b22' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${showDuplicatesOnly ? '#ff6b6b' : 'rgba(255,255,255,0.08)'}`,
                color: showDuplicatesOnly ? '#ff6b6b' : '#888',
                borderRadius: 3,
                padding: '2px 8px',
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              Dups only
            </button>
            <button
              type="button"
              onClick={toggleAll}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#888',
                borderRadius: 3,
                padding: '2px 8px',
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              {activeTypes.size === ALL_TYPES.length ? 'None' : 'All'}
            </button>
            <button
              type="button"
              onClick={copyAll}
              title="Copy the visible visual events (with duplicate markers) to clipboard"
              style={{
                background: copied ? '#51cf6622' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${copied ? '#51cf66' : 'rgba(255,255,255,0.08)'}`,
                color: copied ? '#51cf66' : '#888',
                borderRadius: 3,
                padding: '2px 8px',
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={clear}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#888',
                borderRadius: 3,
                padding: '2px 8px',
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Filters */}
        <div
          style={{
            padding: '6px 10px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            maxHeight: 70,
            overflowY: 'auto',
          }}
        >
          {ALL_TYPES.map((type) => (
            <FilterToggle
              key={type}
              type={type}
              active={activeTypes.has(type)}
              count={counts[type] || 0}
              onToggle={() => toggleType(type)}
            />
          ))}
        </div>

        {/* Event list */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: '#555',
                fontSize: 12,
              }}
            >
              {showDuplicatesOnly ? 'No duplicates detected yet.' : 'No visual events recorded.'}
            </div>
          ) : (
            filtered.map((event, i) => (
              <EventRow
                key={event.id}
                event={event}
                isLatest={i === filtered.length - 1}
              />
            ))
          )}
        </div>

        {/* Legend */}
        <div
          style={{
            padding: '6px 10px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            fontSize: 10,
            color: '#666',
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span>Red left border = duplicate</span>
          <span>Hover DUP badge for original event ID</span>
        </div>
      </div>
  );
}
